import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
type PaymentType = 'sofort' | 'raten';

interface AccountingPackage {
  id: string;
  name: string;
  units: number;
  discountPercent?: number;
  immediateDiscountPercent?: number;
  immediatePriceCents?: number;
  installmentPriceCents?: number;
  totalCentsImmediate?: number;
  totalCentsInstallment?: number;
  installmentEnabled?: boolean;
  installmentMonths?: number;
  highlighted?: boolean;
  highlightLabel?: string;
  defaultPaymentType?: 'immediate' | 'installment';
}

interface PackagePrice {
  package: AccountingPackage;
  totalCents: number;
  currency: string;
}

export interface MigrationPackageListProps {
  packagePrices: PackagePrice[];
}

function formatEuro(cents: number, currency: string): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} ${currency}`;
}

function packageKind(pkg: PackagePrice['package']): 'flexi' | 'm' | 'l' {
  const id = String(pkg.id || '').toUpperCase();
  const name = String(pkg.name || '').toLowerCase();
  if (id.startsWith('S-') || name.includes('flexi') || name.includes('s-paket')) return 'flexi';
  if (id.startsWith('L-') || name.includes('l-paket')) return 'l';
  return 'm';
}

export default function MigrationPackageList({ packagePrices }: Readonly<MigrationPackageListProps>) {
  const [paymentTypeByPackage, setPaymentTypeByPackage] = useState<Record<string, PaymentType>>({});

  useEffect(() => {
    if (packagePrices.length === 0) {
      setPaymentTypeByPackage({});
      return;
    }
    setPaymentTypeByPackage(() => {
      const next: Record<string, PaymentType> = {};
      for (const pp of packagePrices) {
        const pkg = pp.package;
        next[String(pkg.id)] = pkg.defaultPaymentType === 'installment' ? 'raten' : 'sofort';
      }
      return next;
    });
  }, [packagePrices]);

  if (packagePrices.length === 0) {
    return (
      <div className="rounded-md border border-border/70 p-3 text-sm text-muted-foreground">
        Keine Folgepakete für diese Unterrichtsform verfügbar.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {packagePrices.map((pp) => {
        const pkg = pp.package;
        const pkgId = String(pkg.id);
        const kind = packageKind(pkg);
        const paymentType = paymentTypeByPackage[pkgId] ?? 'sofort';
        const totalImmediateCents = Number(pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? pp.totalCents);
        const totalInstallmentCents = Number(pkg.totalCentsInstallment ?? pkg.installmentPriceCents ?? totalImmediateCents);
        const hasInstallments = Boolean(
          (pkg.installmentEnabled ?? true) &&
          (pkg.installmentMonths ?? 0) > 0 &&
          totalInstallmentCents > 0,
        );
        const configuredDiscountPercent = Math.max(
          0,
          Math.round(Number(pkg.immediateDiscountPercent ?? pkg.discountPercent ?? 0)),
        );
        const derivedDiscountPercent = totalInstallmentCents > totalImmediateCents
          ? Math.round((1 - totalImmediateCents / totalInstallmentCents) * 100)
          : 0;
        const immediateDiscountPercent = configuredDiscountPercent > 0
          ? configuredDiscountPercent
          : derivedDiscountPercent;
        const originalImmediateCents = totalInstallmentCents > totalImmediateCents
          ? totalInstallmentCents
          : immediateDiscountPercent > 0 && totalImmediateCents > 0
            ? Math.round(totalImmediateCents / (1 - immediateDiscountPercent / 100))
            : totalImmediateCents;
        const baseTotalCents = paymentType === 'raten' && hasInstallments ? totalInstallmentCents : totalImmediateCents;
        const baseOriginalCents = paymentType === 'sofort' ? originalImmediateCents : totalInstallmentCents;
        const displayUnitCents = pkg.units > 0 ? Math.round(baseTotalCents / pkg.units) : 0;
        const displayOriginalUnitCents = pkg.units > 0 ? Math.round(baseOriginalCents / pkg.units) : 0;
        const hasDiscount = paymentType === 'sofort' && baseOriginalCents > baseTotalCents;
        const installmentCount = Math.max(0, Math.round(Number(pkg.installmentMonths ?? 0)));
        const perInstallmentCents = installmentCount > 0
          ? Math.round(totalInstallmentCents / installmentCount)
          : 0;
        const isHighlighted = pkg.highlighted === true;
        const highlightLabel = pkg.highlightLabel || 'Hero';

        return (
          <Card
            key={pkgId}
            className={cn('h-full border-border', isHighlighted ? 'ring-1 ring-red-500/60' : '')}
          >
            <CardHeader className="py-3">
              <CardTitle className="flex items-center gap-2 text-sm leading-tight">
                {pkg.name}
                {isHighlighted ? (
                  <span className="rounded bg-red-700 px-2 py-0.5 text-xs text-white">{highlightLabel}</span>
                ) : null}
              </CardTitle>
              <div className="text-xs leading-tight text-muted-foreground">
                {kind === 'flexi' ? 'Flexi (S-Paket)' : kind === 'l' ? 'L-Paket' : 'M-Paket'}
                {pkg.units > 0 ? ` · ${pkg.units} Einheiten` : ''}
                {hasDiscount ? ` · ${immediateDiscountPercent}% Rabatt` : ''}
              </div>
            </CardHeader>
            <CardContent className="py-3">
              <div className="space-y-2 text-xs">
                {hasInstallments && kind !== 'flexi' ? (
                  <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
                    <button
                      type="button"
                      onClick={() => setPaymentTypeByPackage((prev) => ({ ...prev, [pkgId]: 'sofort' }))}
                      className={cn(
                        'rounded px-2.5 py-1 text-[11px]',
                        paymentType === 'sofort' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                      )}
                    >
                      Sofort
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentTypeByPackage((prev) => ({ ...prev, [pkgId]: 'raten' }))}
                      className={cn(
                        'rounded px-2.5 py-1 text-[11px]',
                        paymentType === 'raten' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                      )}
                    >
                      Raten
                    </button>
                  </div>
                ) : null}

                {kind === 'flexi' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Paketpreis</span>
                      <span className="font-semibold">{formatEuro(baseTotalCents, pp.currency)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Einheiten</span>
                      <span>{pkg.units}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Preis/Einheit</span>
                      <span>{formatEuro(displayUnitCents, pp.currency)}</span>
                    </div>
                  </>
                ) : null}

                {kind !== 'flexi' && paymentType === 'raten' && hasInstallments ? (
                  <div className="text-muted-foreground">
                    {installmentCount > 0
                      ? `${installmentCount} verbrauchsabhängige Raten je ${formatEuro(perInstallmentCents, pp.currency)}`
                      : 'Verbrauchsabhängige Ratenzahlung.'}
                  </div>
                ) : null}

                {kind !== 'flexi' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Preis/Einheit</span>
                      <div className="flex items-center gap-2">
                        {hasDiscount ? (
                          <span className="text-muted-foreground line-through">
                            {formatEuro(displayOriginalUnitCents, pp.currency)}
                          </span>
                        ) : null}
                        <span className={hasDiscount ? 'font-semibold text-primary' : ''}>
                          {formatEuro(displayUnitCents, pp.currency)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between font-semibold">
                      <span className="font-normal text-muted-foreground">Paketpreis</span>
                      <div className="flex items-center gap-2">
                        {hasDiscount ? (
                          <span className="text-xs font-normal text-muted-foreground line-through">
                            {formatEuro(baseOriginalCents, pp.currency)}
                          </span>
                        ) : null}
                        <span className={hasDiscount ? 'text-primary' : ''}>
                          {formatEuro(baseTotalCents, pp.currency)}
                        </span>
                      </div>
                    </div>
                    {hasDiscount ? (
                      <div className="text-muted-foreground">
                        Ersparnis: {formatEuro(baseOriginalCents - baseTotalCents, pp.currency)}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
